import {
  aiTiktokVideoURL,
  aiTwinGeneratorURL,
  aiUgcVideoURL,
  aiVideoEditURL,
  avatarVideoProURL,
  productAvatarURL,
  videoGenerateURL,
  videoTranslateURL,
} from '@/utils/routeConstants';

import { SEOHeadProps } from '../components/SEOHead';
import { FAQItem } from '../components/templates/FAQSection';
import { FeatureItem } from '../components/templates/FeaturesSection';
import { StepData } from '../components/templates/HowToSection';
import { ToolItem } from '../components/templates/TikTokHeroSupportingSection';
import { ReasonItem } from '../components/templates/WhyChooseSection';
import { ScaleTabData } from '../components/UGCGenerator/UGCScaleSection';
import {
  createBreadcrumbSchema,
  createFAQSchema,
  createHowToSchema,
  createSoftwareAppSchema,
  createWebPageSchema,
} from '../utils/structuredData';

const PAGE_URL = 'https://falcocut.ai/facebook-video-ad-creator';
const PAGE_TITLE =
  'Facebook Video Ad Generator - Create High‑Converting Meta Video Ads With AI';
const PAGE_DESCRIPTION =
  "Launch high-quality Facebook video ads faster than ever. Use FalcoCut's AI to generate spec-ready, engaging video content from simple text or images, allowing you to go from idea to live ad in just a few clicks.";

/** 首屏卡片图片配置 */
export const HERO_CARD_IMAGES: Record<string, { imageUrl: string; alt: string }> = {
  'ugc-video-ads': {
    imageUrl:
      'https://assets.falcocut.ai/facebook-video-ad-creator/facebook-video-ads.png',
    alt: 'Facebook Video Ads',
  },
  'video-from-product-image': {
    imageUrl:
      'https://assets.falcocut.ai/facebook-video-ad-creator/video-from-product-image.png',
    alt: 'Video from Product Image',
  },
  'video-from-avatar-image': {
    imageUrl:
      'https://assets.falcocut.ai/facebook-video-ad-creator/video-from-avatar-image.png',
    alt: 'Video from Avatar Image',
  },
  'generate-image': {
    imageUrl: 'https://assets.falcocut.ai/facebook-video-ad-creator/generate-image.png',
    alt: 'Generate Image',
  },
};

/** Hero Section 配置 */
export const HERO_CONFIG = {
  title: 'AI Facebook Video Ad Generator',
  subtitle:
    "Scale your marketing with FalcoCut's AI Facebook video ad generator. Transform your product shots or ideas into high-converting, professional ads in minutes and maximize your brand reach without the editing hassle.",
};

/** Hero Supporting Section 配置 */
export const HERO_SUPPORTING_CONFIG = {
  title: 'What Kind of Video Ads Can FalcoCut Make',
  subtitle:
    'Transform your static assets and rough ideas into engaging Facebook video ads that capture attention and drive conversions. FalcoCut streamlines your entire creative process, helping you launch ad campaigns quickly with minimal back-and-forth.',
  ctaText: 'Create Facebook Video Ads',
};

export const HERO_SUPPORTING_TOOLS: ToolItem[] = [
  {
    title: 'Photo to Video',
    description:
      'Upload your product shots and let AI animate them into polished product demos, lifestyle clips, and high‑performing ad variations for rapid testing.',
    imageUrl: 'https://assets.falcocut.ai/facebook-video-ad-creator/photo-to-video.png',
  },
  {
    title: 'Script to Video',
    description:
      'Turn your marketing copy into Facebook feed ads, Reels creatives, and UGC‑style promo videos instantly with timed visuals and clean transitions.',
    imageUrl: 'https://assets.falcocut.ai/facebook-video-ad-creator/script-to-video.png',
  },
  {
    title: 'Product Avatars',
    description:
      'Choose or create a realistic avatar that naturally interacts with your product to deliver personalized spokesperson ads and trustworthy product showcases.',
    imageUrl: 'https://assets.falcocut.ai/facebook-video-ad-creator/product-avatars.png',
  },
];

/** How To Section 配置 */
export const HOW_TO_CONFIG = {
  title: 'How to Create a Facebook Video Ad with AI',
  ctaText: 'Create Facebook Video Ads',
  imageUrl:
    'https://assets.falcocut.ai/facebook-video-ad-creator/how-to-create-a-facebook-video-ad-with-ai.png',
};

export const HOW_TO_STEPS: StepData[] = [
  {
    number: 1,
    title: 'Input Your Creative Concept',
    description:
      'Enter a text description of your ad or upload a product image. FalcoCut will immediately process your input to generate a native UGC-style video or animate your static photo into a dynamic scene.',
  },
  {
    number: 2,
    title: 'Select Your AI Model',
    description:
      "Choose from industry-leading models like Sora, Veo, or Kling to define your video's motion and realism. So you can match the specific visual energy required for various video ads.",
  },
  {
    number: 3,
    title: 'Configure Format & Duration',
    description:
      "Set your video length and adjust technical specifications like audio generation, aspect ratio and resolution. These settings ensure your content is perfectly optimized for Facebook's Feed, Stories, or Reel placements before you finalize the render.",
  },
  {
    number: 4,
    title: 'Generate & Launch',
    description:
      'Hit "Start" to let the AI build your video, then download the high-definition file once complete. Your professional ad is now ready to be uploaded directly to Facebook Ads Manager or shared across social channels.',
  },
];

/** Features Section 配置 */
export const FEATURES_CONFIG = {
  title: 'Build Facebook Video Ads that Actually Convert',
  subtitle:
    'FalcoCut combines powerful AI generation with intuitive editing tools to streamline your entire Facebook marketing workflow. From initial concept to the final export, FalcoCut ensures your video ads are professional, engaging, and ready to convert.',
  ctaText: 'Create Facebook Video Ads',
};

export const FEATURES_LIST: FeatureItem[] = [
  {
    title: 'Generate Top-Selling Ads Instantly from Text',
    description:
      'Turning raw marketing copy into high-impact Facebook Feed ads or product explainers is as simple as entering a description into FalcoCut. Our AI interprets every script to generate unique visual scenes and smooth motion, enabling the testing of multiple creative angles without the overhead of traditional filming. This capability allows marketing teams to pivot strategies instantly, ensuring no trending opportunity is lost to slow production cycles. By eliminating studio fees and managing the visual heavy lifting, our text-to-video converter maintains a constant stream of fresh, scroll-stopping content that keeps audiences engaged.',
    imageUrl:
      'https://assets.falcocut.ai/facebook-video-ad-creator/generate-top-selling-ads-instantly-from-text.png',
    imageAlt: 'Generate Top-Selling Ads Instantly from Text',
    ctaText: 'Create Facebook Video Ads',
  },
  {
    title: 'Animate Product Shots into Pro Ad Content',
    description:
      'Transform static product images into dynamic Video Link Ads or "Cinematic" showcases by using our animation engine to add realistic depth and movement. This automated process creates the "thumb-stop" effect necessary to win more auctions in the competitive Facebook environment without requiring a specialized motion graphics team. Breathing new life into existing image libraries allows for the maximization of ROI from past photo shoots. FalcoCut helps small brands and agencies to project a high-end, "big budget" image that builds immediate authority and buyer confidence.',
    imageUrl:
      'https://assets.falcocut.ai/facebook-video-ad-creator/animate-product-shots-into-pro-ad-content.png',
    imageAlt: 'Animate Product Shots into Pro Ad Content',
    ctaText: 'Create Facebook Video Ads',
  },
  {
    title: 'Engage Your Audience with Lifelike AI Avatars',
    description:
      'Humanizing a brand becomes effortless when E-commerce founders feature lifelike digital presenters to deliver unboxing-style messages or "founder" stories directly to Facebook Stories and Reels. These AI-driven characters provide a relatable, person-first feel that builds trust and increases engagement without hiring expensive influencers or booking external actors. By leveraging these realistic avatars, marketers can effectively remove the "talent bottleneck" from the creative pipeline, allowing for the scaling of personalized video outreach. This approach ensures your brand always has a consistent, professional face that speaks directly to your customer\'s needs.',
    imageUrl:
      'https://assets.falcocut.ai/facebook-video-ad-creator/engage-your-audience-with-lifelike-ai-avatars.png',
    imageAlt: 'Engage Your Audience with Lifelike AI Avatars',
    ctaText: 'Create Facebook Video Ads',
  },
  {
    title: 'Add Multilingual Voiceovers with Auto-Subtitles',
    description:
      'Scale your reach across global markets by generating human-sounding voiceovers and accurate auto-subtitles for localized product demos and personalized ads. Since a vast majority of mobile users browse the Facebook News Feed with sound off, these AI-generated captions ensure your value proposition remains accessible in 30+ languages. FalcoCut enables you to open up massive new revenue streams by making your products sellable in international markets with zero extra effort. With our intelligent platform, independent sellers and small businesses can finally compete on a global stage and launch campaigns with unmatched speed.',
    imageUrl:
      'https://assets.falcocut.ai/facebook-video-ad-creator/add-multilingual-voiceovers-with-auto‑subtitles.png',
    imageAlt: 'Add Multilingual Voiceovers with Auto-Subtitles',
    ctaText: 'Create Facebook Video Ads',
  },
  {
    title: 'Edit Video Creatives Using Simple Commands',
    description:
      'Refine your creative assets instantly by using text commands to swap backgrounds or re-style the entire video for A/B testing different aesthetics. FalcoCut allows media buyers to iterate on your Facebook ads based on real-time performance data without starting the production process from scratch. Adopting such a rapid iteration cycle enables teams to "double down" on winning creatives in minutes rather than days. Our video editor gives growth marketers a significant competitive advantage by optimizing ad spend based on the specific hooks and styles that are converting in the moment.',
    imageUrl:
      'https://assets.falcocut.ai/facebook-video-ad-creator/edit-video-creatives-using-simple-commands.png',
    imageAlt: 'Edit Video Creatives Using Simple Commands',
    ctaText: 'Create Facebook Video Ads',
  },
  {
    title: 'Ad\u2011ready Formats Optimized for Facebook',
    description:
      'Ensure every export meets strict Meta specifications by automatically adjusting your videos for 1:1 Feed, 9:16 Reels, and 16:9 desktop placements. FalcoCut handles the technical heavy lifting of aspect ratios and bitrates so your ads never suffer from awkward cropping or a low-quality look. This guarantees that your brand always looks its best, preventing the "unprofessional" feel that leads to rejected ads or low click-through rates. You can launch different types of video ads with total confidence, knowing your content is technically perfect for every corner of the Facebook ecosystem.',
    imageUrl:
      'https://assets.falcocut.ai/facebook-video-ad-creator/ad‑ready-formats-optimized-for-facebook.png',
    imageAlt: 'Ad\u2011ready Formats Optimized for Facebook',
    ctaText: 'Create Facebook Video Ads',
  },
];

/** Video Showcase Section 配置 */
export const EXAMPLE_VIDEOS_CONFIG = {
  title: 'Visual Inspiration for Every Facebook Ad Strategy',
  subtitle:
    'Multiply your creative variations with a wide range of video styles engineered to stop the scroll and drive conversions across the Meta ecosystem. From authentic UGC-style testimonials to high-end cinematic product spotlights, these examples illustrate how our AI-driven engine transforms diverse marketing concepts into professional, platform-ready assets.',
  ctaText: 'Create Facebook Video Ads',
};

export const EXAMPLE_VIDEOS = [
  {
    id: 'video-1',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-facemask.mp4',
    posterUrl: '',
    alt: 'Facebook Video Ad Example 1',
  },
  {
    id: 'video-2',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-earbuds.mp4',
    posterUrl: '',
    alt: 'Facebook Video Ad Example 2',
  },
  {
    id: 'video-3',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-outfit.mp4',
    posterUrl: '',
    alt: 'Facebook Video Ad Example 3',
  },
  {
    id: 'video-4',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-cream.mp4',
    posterUrl: '',
    alt: 'Facebook Video Ad Example 4',
  },
  {
    id: 'video-5',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-perfume.mp4',
    posterUrl: '',
    alt: 'Facebook Video Ad Example 5',
  },
  {
    id: 'video-6',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-energy-drink.mp4',
    posterUrl: '',
    alt: 'Facebook Video Ad Example 6',
  },
  {
    id: 'video-7',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-product-review-reaction.mp4',
    posterUrl: '',
    alt: 'Facebook Video Ad Example 7',
  },
];

/** Use Cases Section 配置 */
export const USE_CASES_CONFIG = {
  title: 'Creative Solutions for Every Fackbook Ad Campaign',
  subtitle:
    'Take full control of your creative pipeline with a unified suite of tools designed to transform ideas into high-performing Facebook assets. Whether starting from scratch or refining existing content, our platform provides the flexibility to build, polish, and tailor every video to your exact specifications.',
};

export const USE_CASES_TABS: ScaleTabData[] = [
  {
    key: 'viral-video-testing',
    label: 'Viral Video Testing',
    title: 'Viral Video Testing',
    description:
      'Convert a single product shot into ten different "hook" variations to see which one stops the scroll most effectively. Media buyers can identify winning creative angles in 24 hours rather than spending weeks on manual testing and production.',
    imageUrl:
      'https://assets.falcocut.ai/facebook-video-ad-creator/viral-video-testing.png',
    ctaText: 'Create Your Video Ad Now',
    ctaUrl: '/ai-video-generator',
  },
  {
    key: 'pro-level-ad-refinement',
    label: 'Pro-Level Ad Refinement',
    title: 'Pro-Level Ad Refinement',
    description:
      'Transform raw or amateur footage into studio-quality content by using simple commands to swap backgrounds and clean up visuals. This ensures that even the simplest clips project a high-end brand image that builds immediate authority and buyer confidence on the feed.',
    imageUrl:
      'https://assets.falcocut.ai/facebook-video-ad-creator/pro-level-ad-refinement.png',
    ctaText: 'Start Editing Now',
    ctaUrl: '/ai-video-editor',
  },
  {
    key: 'global-market-expansion',
    label: 'Global Market Expansion',
    title: 'Global Market Expansion',
    description:
      'Take a successful domestic ad and instantly localize it for five different international regions with native-sounding voiceovers and translated captions. Brands can enter new countries with cultural relevance immediately, without the friction of hiring localized talent or agencies.',
    imageUrl:
      'https://assets.falcocut.ai/facebook-video-ad-creator/global-market-expansion.png',
    ctaText: 'Translate Your Video Now',
    ctaUrl: '/video-translator',
  },
];

/** Why Choose Section 配置 */
export const WHY_CHOOSE_CONFIG = {
  title: 'Why Choose FalcoCut AI Facebook Video Ad Generator',
  subtitle:
    'Use FalcoCut to equip your business with a high-velocity production engine that eliminates the traditional barriers of cost, time, and technical complexity in video marketing.',
  ctaText: 'Create Facebook Video Ads',
};

export const WHY_CHOOSE_REASONS: ReasonItem[] = [
  {
    title: 'Fast Video Generation',
    description:
      "Don't wait weeks for a production team to film, edit, and deliver your creative content. Generate polished, platform-ready ads in just minutes so your campaigns can go live and start generating sales immediately.",
  },
  {
    title: 'Go Global Instantly',
    description:
      'Forget the logistical headache of hiring translation agencies or regional voice talent for international sets. Make your products sellable in over 30 languages instantly to reach a massive global audience with zero extra effort.',
  },
  {
    title: 'Simple to Use',
    description:
      'Skip the steep learning curve and frustration of mastering complex professional editing software. The intuitive interface handles all the technical heavy lifting so anyone can produce a high-performing video in just a few clicks.',
  },
  {
    title: 'Create Unlimited Variations',
    description:
      'Stop struggling to keep up with ad fatigue by trying to film multiple versions of the same hook. Produce dozens of unique creative iterations in a single afternoon while traditional creators are still stuck in the planning phase.',
  },
  {
    title: 'Stay On-Brand',
    description:
      "Avoid the inconsistency of working with different influencers who might not capture your brand's true voice. Utilize digital presenters who are available 24/7 to deliver your message with a perfect, professional look every single time.",
  },
  {
    title: 'Cost-Effective Solution',
    description:
      'Stop overpaying for expensive studio rentals, camera crews, and professional actors. Create high-converting video assets at a fraction of the usual price so more of your budget goes directly into your ad spend.',
  },
];

/** FAQ Section 配置 */
export const FAQ_CONFIG = {
  title: 'Frequently Asked Questions',
};

export const FAQ_LIST: FAQItem[] = [
  {
    question: 'How do Facebook video ads work?',
    answer:
      "Facebook video ads work by placing short, targeted videos directly in users' feeds, Stories, or in\u2011stream placements. These ads reach audiences based on detailed demographic, interest, and behavior targeting chosen by the advertiser. When viewers watch, click, or engage with the video, Facebook optimizes delivery to people most likely to respond. This system helps businesses increase visibility, drive conversions, and build stronger brand awareness across the platform.",
  },
  {
    question: 'How to create a Facebook video ad?',
    answer:
      'Step 1: Enter a short text description or upload a product image to begin. You can also start with selecting an avatar from FalcoCut\'s library to make video ads that feels genuinely creator-made.\n\nStep 2: Choose from advanced models you prefer to shape motion and realism. Each model helps match the visual energy required for different ad styles.\n\nStep 3: Set your video length and adjust key technical settings like aspect ratio and resolution. These options ensure your content is optimized for Feed, Stories, or Reels.\n\nStep 4: Click "Start" to let FalcoCut build your final video automatically. Download the high\u2011definition file and upload it directly to Facebook Ads Manager.',
  },
  {
    question: 'What type of Facebook video ads can I create?',
    answer:
      'Facebook supports many video ad formats, including feed ads, Reels creatives, Stories placements, and in\u2011stream video ads. You can also create product demos, testimonial videos, carousel\u2011style clips, and UGC\u2011inspired promotional content. Each format serves different marketing goals, from driving awareness to increasing conversions or retargeting warm audiences. FalcoCut helps you produce these variations quickly so you can test multiple creative angles with ease.',
  },
  {
    question: 'Do I need design or editing skills to use FalcoCut?',
    answer:
      'No. FalcoCut is designed so anyone can create professional video ads without prior design or editing experience. The platform handles complex tasks like timing visuals, generating voiceovers, and animating product shots automatically. You simply provide text, images, or product details, and the system builds a polished video for you. This makes high\u2011quality ad creation accessible to small businesses, independent sellers, and marketers with limited production resources.',
  },
  {
    question: "What's the best format and size for a Facebook ad video?",
    answer:
      'Facebook recommends using MP4 or MOV formats because they provide reliable quality and smooth playback across all placements. Videos should be at least 1080\u00d71080 pixels to ensure clarity on mobile and desktop screens. Square or vertical formats generally perform better since most Facebook users browse on mobile devices. Keeping file sizes optimized also helps your ads load quickly and maintain strong viewer engagement.',
  },
  {
    question: 'How long should a Facebook video ad be?',
    answer:
      'Shorter videos typically perform best on Facebook because users scroll quickly and respond to concise messaging. Aim for 5\u201315 seconds to deliver your core message without losing viewer attention. Longer videos can still work, but they require stronger storytelling and more compelling visuals to maintain interest. Prioritizing clarity and pacing helps ensure your message lands effectively within a limited attention window.',
  },
  {
    question: 'Are there any best practices for creating Facebook video ad?',
    answer:
      'Effective Facebook video ads start with a strong hook that captures attention within the first few seconds. Use clear messaging, bold visuals, and mobile\u2011friendly framing to keep viewers engaged throughout the video. Adding subtitles is essential because many users watch with sound off, especially in feed placements. Testing multiple variations helps you identify which creative elements resonate most with your audience and drive better results.',
  },
  {
    question: 'Which Facebook ads are most effective?',
    answer:
      "The most effective Facebook ads are those that match user behavior, platform trends, and your campaign's specific goals. Short product demos, UGC\u2011style videos, and testimonial clips often perform well because they feel authentic and easy to understand. Ads that highlight clear benefits and show the product in action typically generate higher engagement and conversions. Consistent testing and optimization help you discover which formats deliver the strongest performance for your audience.",
  },
  {
    question: 'Can I customize my AI-generated Facebook video ads?',
    answer:
      'Yes, you can fully customize your AI\u2011generated Facebook video ads on FalcoCut to match your brand style, messaging, and campaign goals. FalcoCut allows you to adjust visuals, re-generate clips, change layouts, and swap products or backgrounds with ease. You can also refine pacing, add subtitles, and choose different voiceovers to create a more personalized final video. These flexible editing options ensure every ad feels uniquely yours while still benefiting from AI\u2011powered optimization.',
  },
];

/** Explore Tools Section 配置 */
export interface ExploreToolTag {
  title: string;
  link: string;
}

export const EXPLORE_TOOLS: ExploreToolTag[] = [
  { title: 'AI Video Generator', link: videoGenerateURL },
  { title: 'AI Video Editor', link: aiVideoEditURL },
  { title: 'AI Video Translator', link: videoTranslateURL },
  { title: 'AI Avatar Generator', link: avatarVideoProURL },
  { title: 'Product Avatar', link: productAvatarURL },
  { title: 'AI TikTok Video Generator', link: aiTiktokVideoURL },
  { title: 'AI UGC Video Generator', link: aiUgcVideoURL },
  { title: 'AI Twin Generator', link: aiTwinGeneratorURL },
];

/** Footer CTA Section 配置 */
export const FOOTER_CTA_CONFIG = {
  title: 'Create the Best Facebook Video Ads with AI',
  subtitle:
    'Ready to upgrade your video ad creation? Use FalcoCut to streamline production, reduce expenses, and maximize ROI across your Facebook marketing campaigns.',
  ctaText: 'Create Facebook Video Ads Now',
};

/** SEO 配置 */
export const SEO_CONFIG: SEOHeadProps = {
  title: PAGE_TITLE,
  description: PAGE_DESCRIPTION,
  url: PAGE_URL,
  publishedTime: '2026-02-27',
  modifiedTime: '2026-03-24',
  structuredData: [
    createSoftwareAppSchema({
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
    }),
    createWebPageSchema({
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      datePublished: '2026-02-27',
      dateModified: '2026-03-24',
    }),
    createFAQSchema({
      faqs: FAQ_LIST.map((f) => ({
        question: f.question,
        answer: f.answer || '',
      })),
    }),
    createBreadcrumbSchema({
      items: [
        { name: 'Home', url: 'https://falcocut.ai' },
        { name: 'Facebook Video Ad Creator', url: PAGE_URL },
      ],
    }),
    createHowToSchema({
      name: HOW_TO_CONFIG.title,
      steps: HOW_TO_STEPS.map((s) => ({ name: s.title, text: s.description })),
    }),
  ],
};
